import { type MatrixEvent, EventStatus, RelationType, M_POLL_START } from "matrix-js-sdk/src/matrix";

import { MatrixClientPeg } from "../../../MatrixClientPeg";
import { isUrlPermitted } from "../../../HtmlUtils";
import { getShareableLocationEvent } from "../../../events/location/getShareableLocationEvent";
import { isPollEnded } from "./MPollBody";
import SettingsStore from "../../../settings/SettingsStore";
import { type IEventTileOps } from "../rooms/EventTile";
import { isContentActionable, canEditContent } from "../../../utils/EventUtils";
import PinningUtils from "../../../utils/PinningUtils";
import { getForwardableEvent } from "../../../events/forward/getForwardableEvent";
import { DthSessionStorage } from "../../../dth/clientHttp/DthSessionStorage";
import { MediaEventHelper } from "../../../utils/MediaEventHelper";

// ──── Checks for actions visible in MessageActionBar ──────────────────────────────────────────

/**
 * Whether the Edit button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @return {boolean} True if the Edit button should be shown.
 */
export function canShowEditButton(mxEvent: MatrixEvent): boolean {
    return canEditContent(MatrixClientPeg.safeGet(), mxEvent);
}

/**
 * Whether the Pin/Unpin button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @return {boolean} True if the Pin/Unpin button should be shown.
 */
export function canShowPinButton(mxEvent: MatrixEvent): boolean {
    const cli = MatrixClientPeg.safeGet();
    return PinningUtils.canPin(cli, mxEvent) || PinningUtils.canUnpin(cli, mxEvent);
}

/**
 * Whether the Reply button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @param {boolean} canSendMessages - Whether the user can send messages.
 * @param {boolean} [isContentActionableCheck] - Optional precomputed actionable check.
 * @return {boolean} True if the Reply button should be shown.
 */
export function canShowReplyButton(mxEvent: MatrixEvent, canSendMessages: boolean, isContentActionableCheck?: boolean): boolean {
    if (isContentActionableCheck === undefined) {
        isContentActionableCheck = isContentActionable(mxEvent);
    }
    return isContentActionableCheck && canSendMessages;
}

/**
 * Whether the React button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @param {boolean} canReact - Whether the user can react.
 * @param {boolean} isSearch - Whether the context is a search.
 * @param {boolean} [isContentActionableCheck] - Optional precomputed actionable check.
 * @return {boolean} True if the React button should be shown.
 */
export function canShowReactButton(mxEvent: MatrixEvent, canReact: boolean, isSearch: boolean, isContentActionableCheck?: boolean): boolean {
    if (isContentActionableCheck === undefined) {
        isContentActionableCheck = isContentActionable(mxEvent);
    }
    return isContentActionableCheck && canReact && !isSearch;
}

/**
 * Whether the Forward button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @param {boolean} [isContentActionableCheck] - Optional precomputed actionable check.
 * @return {boolean} True if the Forward button should be shown.
 */
export function canShowForwardButton(mxEvent: MatrixEvent, isContentActionableCheck?: boolean): boolean {
    const cli = MatrixClientPeg.safeGet();
    const privateConfiguration = DthSessionStorage.getPrivateConfiguration();
    const enableForwardMessages = privateConfiguration?.allowMessageForwarding ?? false;
    if (isContentActionableCheck === undefined) {
        isContentActionableCheck = isContentActionable(mxEvent);
    }
    return enableForwardMessages && isContentActionableCheck && !!getForwardableEvent(mxEvent, cli);
}

/**
 * Whether the Download button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @param {boolean} [isContentActionableCheck] - Optional precomputed actionable check.
 * @return {boolean} True if the Download button should be shown.
 */
export function canShowDownloadButton(mxEvent: MatrixEvent, isContentActionableCheck?: boolean): boolean {
    if (isContentActionableCheck === undefined) {
        isContentActionableCheck = isContentActionable(mxEvent);
    }
    return isContentActionableCheck && MediaEventHelper.isEligible(mxEvent);
}

/**
 * Whether the Hide button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @param {boolean} [isContentActionableCheck] - Optional precomputed actionable check.
 * @return {boolean} True if the Hide button should be shown.
 */
export function canShowHideButton(mxEvent: MatrixEvent, isContentActionableCheck?: boolean): boolean {
    if (isContentActionableCheck === undefined) {
        isContentActionableCheck = isContentActionable(mxEvent);
    }
    return isContentActionableCheck && MediaEventHelper.canHide(mxEvent);
}

/**
 * Whether the Delete button should be shown in MessageActionBar.
 * Shown only for own sent messages when no cancel action is pending.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @param {boolean} allowCancel - Whether a cancel action is pending.
 * @return {boolean} True if the Delete button should be shown.
 */
export function canShowDeleteButton(mxEvent: MatrixEvent, allowCancel: boolean): boolean {
    const me = MatrixClientPeg.safeGet().getUserId();
    return mxEvent.getSender() === me && !allowCancel;
}

// ──── Checks for extra actions in MessageContextMenu (Options button or right-click) ───────

/**
 * Whether the Delete/Redact action should be shown.
 * Gated by rightClick in the menu since the button is already visible in MessageActionBar.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @param {boolean} canRedact - Whether the user can redact.
 * @return {boolean} True if the Redact button should be shown.
 */
export function canShowRedactButton(mxEvent: MatrixEvent, canRedact: boolean): boolean {
    const me = MatrixClientPeg.safeGet().getUserId();
    const isSent = !mxEvent.status || mxEvent.status === EventStatus.SENT;
    return isSent && canRedact && mxEvent.getSender() === me;
}

/**
 * Whether the Open in Map Site button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @return {boolean} True if the Open in Map Site button should be shown.
 */
export function canShowOpenInMapSiteButton(mxEvent: MatrixEvent): boolean {
    return !!getShareableLocationEvent(mxEvent, MatrixClientPeg.safeGet());
}

/**
 * Whether the End Poll button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @param {boolean} canRedact - Whether the user can redact.
 * @return {boolean} True if the End Poll button should be shown.
 */
export function canShowEndPollButton(mxEvent: MatrixEvent, canRedact: boolean): boolean {
    return M_POLL_START.matches(mxEvent.getType()) && canRedact && !isPollEnded(mxEvent, MatrixClientPeg.safeGet());
}

/**
 * Whether the External URL button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @return {boolean} True if the External URL button should be shown.
 */
export function canShowExternalURLButton(mxEvent: MatrixEvent): boolean {
    const url = mxEvent.getContent().external_url;
    return typeof url === "string" && isUrlPermitted(url);
}

/**
 * Whether the Jump to Related Event button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @return {boolean} True if the Jump to Related Event button should be shown.
 */
export function canShowJumpToRelatedEventButton(mxEvent: MatrixEvent): boolean {
    return !!mxEvent.getAssociatedId() && SettingsStore.getValue("developerMode");
}

/**
 * Whether the Unhide Preview button should be shown.
 * @param {IEventTileOps} [eventTileOps] - Optional event tile operations.
 * @return {boolean} True if the Unhide Preview button should be shown.
 */
export function canShowUnhidePreviewButton(eventTileOps?: IEventTileOps): boolean {
    return !!eventTileOps?.isWidgetHidden();
}

/**
 * Whether the Resend Reactions button should be shown.
 * @param {MatrixEvent} mxEvent - The Matrix event to check.
 * @return {boolean} True if the Resend Reactions button should be shown.
 */
export function canShowResendReactionsButton(mxEvent: MatrixEvent): boolean {
    const cli = MatrixClientPeg.safeGet();
    const room = cli.getRoom(mxEvent.getRoomId());
    const eventId = mxEvent.getId();
    if (mxEvent.isRedacted() || !room || !eventId) return false;
    return room.getPendingEvents().some((e) => {
        const relation = e.getRelation();
        return (
            relation?.rel_type === RelationType.Annotation &&
            relation.event_id === eventId &&
            e.status === EventStatus.NOT_SENT
        );
    });
}

// ──── Aggregated check for Options button visibility ──────────────────────────────────────────

export interface ExtraMenuActionsParams {
    mxEvent: MatrixEvent;
    eventTileOps?: IEventTileOps;
    collapseReplyChain?: () => void;
    canRedact: boolean;
}

/**
 * Returns true if there are extra actions to show in the Options context menu,
 * i.e. actions that are NOT already visible as buttons in MessageActionBar.
 *
 * These are: openInMapSite, endPoll, externalURL, jumpToRelatedEvent,
 * unhidePreview, resendReactions, collapseReplyChain.
 *
 * Actions already present in MessageActionBar (edit, reply, react, forward,
 * download, hide, delete, pin/unpin, resend/cancel) are excluded from this check.
 *
 * @param {ExtraMenuActionsParams} params - The parameters for extra menu actions.
 * @param {MatrixEvent} params.mxEvent - The Matrix event to check.
 * @param {IEventTileOps} [params.eventTileOps] - Optional event tile operations.
 * @param {() => void} [params.collapseReplyChain] - Optional collapse reply chain function.
 * @param {boolean} params.canRedact - Whether the user can redact.
 * @return {boolean} True if there are extra actions to show.
 */
export function hasExtraMenuActions({
    mxEvent,
    eventTileOps,
    collapseReplyChain,
    canRedact,
}: ExtraMenuActionsParams): boolean {
    return (
        canShowOpenInMapSiteButton(mxEvent) ||
        canShowEndPollButton(mxEvent, canRedact) ||
        canShowExternalURLButton(mxEvent) ||
        canShowJumpToRelatedEventButton(mxEvent) ||
        canShowUnhidePreviewButton(eventTileOps) ||
        canShowResendReactionsButton(mxEvent) ||
        !!collapseReplyChain
    );
}
